import { Outlet, useLocation } from 'react-router-dom';
import { Wifi, WifiOff, Activity } from 'lucide-react';
import { AuthGuard } from '@/src/ui-components';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/src/ui-components/ui/sidebar';
import { Separator } from '@/src/ui-components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '@/src/ui-components/ui/popover';
import { Button } from '@/src/ui-components/ui/button';
import { PrivateProviders } from '@/src/contexts';
import { AppSidebar } from '@/src/ui-components/app-sidebar';
import { ThemeToggle } from '@/src/ui-components/theme-toggle';
import { useXRPL, useHeartbeat } from '@/src/hooks';

// Route name mapping
const routeNames: Record<string, string> = {
  '/trading': 'Trading',
  '/swap': 'Swap',
  '/wallet': 'Wallet',
  '/history': 'History',
  '/oracles': 'Oracles',
  '/preferences': 'Preferences',
};

// Connection Status Component (must be inside providers)
function ConnectionStatus() {
  const { isConnected, client } = useXRPL();
  const heartbeat = useHeartbeat(client, isConnected);

  // Determine status color based on connection and latency
  const getStatusColor = () => {
    if (!isConnected) return 'text-red-600 hover:text-red-700';
    if (heartbeat.latency === null) return 'text-yellow-600 hover:text-yellow-700';
    if (heartbeat.latency < 200) return 'text-green-600 hover:text-green-700';
    if (heartbeat.latency < 500) return 'text-yellow-600 hover:text-yellow-700';
    return 'text-orange-600 hover:text-orange-700';
  };

  // Format latency display
  const formatLatency = (latency: number | null) => {
    if (latency === null) return 'Measuring...';
    return `${latency}ms`;
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={`gap-2 ${getStatusColor()}`}
        >
          {isConnected ? <Wifi className="h-5 w-5" /> : <WifiOff className="h-5 w-5" />}
          {isConnected && (
            <span className="text-sm font-medium">
              {formatLatency(heartbeat.latency)}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto min-w-[240px] p-3" align="end">
        <div className="space-y-3">
          {/* Connection Status */}
          <div className="flex items-center gap-2">
            {isConnected ? (
              <Wifi className="h-4 w-4 text-green-600" />
            ) : (
              <WifiOff className="h-4 w-4 text-red-600" />
            )}
            <div className="flex-1 text-sm">
              {isConnected ? (
                <div>
                  <div className="font-medium">Connected</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {client.url}
                  </div>
                </div>
              ) : (
                <span className="font-medium">Disconnected</span>
              )}
            </div>
          </div>

          {/* Latency Info */}
          {isConnected && (
            <>
              <Separator />
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Latency</span>
                    <span className={`font-medium ${
                      heartbeat.latency === null ? 'text-muted-foreground' :
                      heartbeat.latency < 200 ? 'text-green-600' :
                      heartbeat.latency < 500 ? 'text-yellow-600' :
                      'text-orange-600'
                    }`}>
                      {formatLatency(heartbeat.latency)}
                    </span>
                  </div>
                  {heartbeat.lastPing && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Last ping: {heartbeat.lastPing.toLocaleTimeString()}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Layout Content Component (must be inside providers)
function LayoutContent() {
  const location = useLocation();
  const currentRoute = routeNames[location.pathname] || 'Dashboard';

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex w-full items-center justify-between px-4">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="-ml-1" />
              <Separator orientation="vertical" className="mr-2 h-4" />
              <span className="text-sm font-semibold">{currentRoute}</span>
            </div>

            {/* Right side controls */}
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <ConnectionStatus />
            </div>
          </div>
        </header>
        <div className="flex flex-1 flex-col overflow-hidden pb-4">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

export function ProtectedLayout() {
  return (
    <PrivateProviders>
      <AuthGuard>
        <LayoutContent />
      </AuthGuard>
    </PrivateProviders>
  );
}
